import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faLink, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import './PatientMetadataEditor.css';
import AppProfileModal from '../AppProfileModal/AppProfileModal';
import { useUser } from '../../../UserContext.js';

const GENDER_OPTIONS = ['MALE', 'FEMALE', 'OTHER'];
const ALCOHOL_OPTIONS = ['NONE', 'SOCIALLY', 'REGULARLY'];

function PatientMetadataEditor({ patient, patientId, onClose }) {
    const { user } = useUser();
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const closeTimeoutRef = useRef(null);
    const [appUsers, setAppUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [showInvitePopup, setShowInvitePopup] = useState(false);
    const appUserScrollerRef = useRef(null);
    const [appUserScrollState, setAppUserScrollState] = useState({
        canScrollLeft: false,
        canScrollRight: false
    });

    const effectivePatientId = patient?.patient_id || patientId;

    const [formData, setFormData] = useState({
        patient_id: effectivePatientId || '',
        name: '',
        family_number: '',
        date_of_birth: '',
        gender: '',
        chronic_disease: '',
        drug_consumption: '',
        smoking_pcs_per_day: '',
        alcohol_usage: '',
        height_cm: '',
        weight_kg: '',
        phone: '',
        sample_acceptance_date: '',
        sample_type: '',
        email: ''
    });

    useEffect(() => {
        if (!effectivePatientId) {
            return;
        }

        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 50);

        const fetchAuthoritativeData = async () => {
            try {
                const response = await fetch(`https://service9005.szapfs.org/api/patient/${effectivePatientId}`);
                if (!response.ok) {
                    throw new Error(`Failed to fetch patient data: ${response.statusText}`);
                }
                const freshPatientData = await response.json();

                const sanitizedData = Object.keys(formData).reduce((acc, key) => {
                    acc[key] = freshPatientData[key] || '';
                    return acc;
                }, {});
                setFormData(sanitizedData);

                await fetchAppUsers(effectivePatientId);

            } catch (error) {
                console.error(`Error fetching authoritative data for ID ${effectivePatientId}:`, error);
            }
        };

        fetchAuthoritativeData();

        return () => {
            clearTimeout(timer);
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        };
    }, [effectivePatientId]);


    const fetchAppUsers = async (pId) => {
        try {
            const response = await fetch(`https://service9.szapfs.org/appusers/patientid/${pId}`);
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            const sortedUsers = [...data].sort((a, b) => a.User_ID - b.User_ID);
            setAppUsers(sortedUsers);
            if (sortedUsers.length > 0 && !selectedUser) {
                setSelectedUser(sortedUsers[0]);
            }
        } catch (err) {
            console.error('Error fetching app users:', err);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        closeTimeoutRef.current = setTimeout(onClose, 200);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch('https://service9.szapfs.org/patient/metadata', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            if (!response.ok) {
                throw new Error('Failed to update patient metadata');
            }
            setSaveSuccess(true);
            setTimeout(() => {
                setSaveSuccess(false);
            }, 2000);
        } catch (error) {
            console.error('Error updating patient metadata:', error);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const createAppUserAccount = async () => {
        try {
            const createResponse = await fetch('https://service9.szapfs.org/APM-Create-User', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            if (!createResponse.ok) throw new Error('Failed to create new user account');
    
            const usersResponse = await fetch(`https://service9.szapfs.org/appusers/patientid/${effectivePatientId}`);
            if (!usersResponse.ok) throw new Error('Failed to fetch updated users list after creation');
            
            const freshAppUsers = await usersResponse.json();
            const sortedUsers = [...freshAppUsers].sort((a, b) => a.User_ID - b.User_ID);
            
            setAppUsers(sortedUsers);
            
            if (sortedUsers.length > 0) {
                const newestUser = sortedUsers.reduce((max, user) => 
                    (user.User_ID > max.User_ID) ? user : max, sortedUsers[0]
                );
                return newestUser; 
            }
            return null;
        } catch (error) {
            console.error('Error in createAppUserAccount:', error);
            return null;
        }
    };

    const createNewUser = async () => {
        const newUser = await createAppUserAccount();
        if (newUser) {
            setSelectedUser(newUser);
        }
        setShowInvitePopup(false);
    };

    const handleSendInvitation = async (method) => {
        const newUser = await createAppUserAccount();
        
        if (!newUser) {
            console.error("Invitation process aborted because user creation failed.");
            setShowInvitePopup(false);
            return;
        }
    
        setSelectedUser(newUser);
    
        try {
            const inviteData = { ...formData, method: method };
            const response = await fetch('https://service9005.szapfs.org/api/invite', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(inviteData)
            });
            if (!response.ok) {
                throw new Error(`Failed to send ${method.toLowerCase()} invitation`);
            }
            const result = await response.json();
            console.log(`${method} invitation sent successfully:`, result);
        } catch (error) {
            console.error(`Error sending ${method.toLowerCase()} invitation:`, error);
        }
    
        setShowInvitePopup(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showInvitePopup && !event.target.closest('.APM-chain-container')) {
                setShowInvitePopup(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showInvitePopup]);

    const updateAppUserScrollState = useCallback(() => {
        const scroller = appUserScrollerRef.current;
        if (!scroller) {
            setAppUserScrollState({
                canScrollLeft: false,
                canScrollRight: false
            });
            return;
        }

        const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        const nextState = {
            canScrollLeft: scroller.scrollLeft > 1,
            canScrollRight: scroller.scrollLeft < maxScrollLeft - 1
        };

        setAppUserScrollState(prevState => (
            prevState.canScrollLeft === nextState.canScrollLeft &&
            prevState.canScrollRight === nextState.canScrollRight
                ? prevState
                : nextState
        ));
    }, []);

    useEffect(() => {
        const scroller = appUserScrollerRef.current;
        updateAppUserScrollState();

        if (!scroller) {
            return undefined;
        }

        scroller.addEventListener('scroll', updateAppUserScrollState, { passive: true });
        window.addEventListener('resize', updateAppUserScrollState);

        return () => {
            scroller.removeEventListener('scroll', updateAppUserScrollState);
            window.removeEventListener('resize', updateAppUserScrollState);
        };
    }, [appUsers.length, updateAppUserScrollState]);

    useEffect(() => {
        const selectedCircle = appUserScrollerRef.current?.querySelector('.APM-user-circle.selected');
        selectedCircle?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });

        const timer = setTimeout(updateAppUserScrollState, 250);
        return () => clearTimeout(timer);
    }, [selectedUser, updateAppUserScrollState]);

    const scrollAppUsers = (direction) => {
        const scroller = appUserScrollerRef.current;
        if (!scroller) {
            return;
        }

        scroller.scrollBy({
            left: direction * Math.max(scroller.clientWidth * 0.75, 48),
            behavior: 'smooth'
        });
    };
    
    const modalContent = (
        <div className={`PME-overlay ${isVisible ? 'pme-visible' : ''} ${isClosing ? 'pme-closing' : ''}`}>
            <div className={`PME-container ${isVisible ? 'pme-visible' : ''} ${isClosing ? 'pme-closing' : ''}`}>
                <div className="PME-modal">
                    <div className="PME-header">
                        <FontAwesomeIcon
                            icon={faTimes}
                            className="PME-close"
                            onClick={handleClose}
                        />
                    </div>
                    <form className="PME-form" onSubmit={handleSubmit}>
                        <div className="PME-form-grid">
                            <div className="PME-name-section">
                                <div className="PME-form-group">
                                    <label>Full Name</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        placeholder="Enter patient's full name"
                                    />
                                </div>
                            </div>
                            <div className="PME-contact-section">
                                <div className="PME-form-group">
                                    <label>Phone Number</label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleChange('phone', e.target.value)}
                                        placeholder="+1 (555) 123-4567"
                                    />
                                </div>
                                <div className="PME-form-group">
                                    <label>Email Address</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        placeholder="patient@example.com"
                                    />
                                </div>
                            </div>
                            <div className="PME-details-section">
                                <div className="PME-form-group">
                                    <label>Family Number</label>
                                    <input
                                        type="text"
                                        value={formData.family_number}
                                        onChange={(e) => handleChange('family_number', e.target.value)}
                                        placeholder="F001"
                                    />
                                </div>
                                <div className="PME-form-group">
                                    <label>Date of Birth</label>
                                    <input
                                        type="text"
                                        value={formData.date_of_birth}
                                        onChange={(e) => handleChange('date_of_birth', e.target.value)}
                                        placeholder="DD-MM-YYYY"
                                    />
                                </div>
                                <div className="PME-form-group">
                                    <label>Gender</label>
                                    <select
                                        value={formData.gender}
                                        onChange={(e) => handleChange('gender', e.target.value)}
                                    >
                                        <option value="">Select Gender</option>
                                        {GENDER_OPTIONS.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="PME-form-group">
                                    <label>Height (cm)</label>
                                    <input
                                        type="number"
                                        value={formData.height_cm}
                                        onChange={(e) => handleChange('height_cm', e.target.value)}
                                        placeholder="175"
                                    />
                                </div>
                                <div className="PME-form-group">
                                    <label>Weight (kg)</label>
                                    <input
                                        type="number"
                                        value={formData.weight_kg}
                                        onChange={(e) => handleChange('weight_kg', e.target.value)}
                                        placeholder="70"
                                    />
                                </div>
                                <div className="PME-form-group">
                                    <label>Smoking (pcs/day)</label>
                                    <input
                                        type="number"
                                        value={formData.smoking_pcs_per_day}
                                        onChange={(e) => handleChange('smoking_pcs_per_day', e.target.value)}
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                            <div className="PME-health-section">
                                <div className="PME-form-group">
                                    <label>Alcohol Usage</label>
                                    <select
                                        value={formData.alcohol_usage}
                                        onChange={(e) => handleChange('alcohol_usage', e.target.value)}
                                    >
                                        <option value="">Select Usage</option>
                                        {ALCOHOL_OPTIONS.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="PME-form-group full-width">
                                    <label>Chronic Diseases</label>
                                    <textarea
                                        value={formData.chronic_disease}
                                        onChange={(e) => handleChange('chronic_disease', e.target.value)}
                                        rows={3}
                                        placeholder="List any chronic diseases or medical conditions..."
                                    />
                                </div>
                                <div className="PME-form-group full-width">
                                    <label>Drug Consumption</label>
                                    <textarea
                                        value={formData.drug_consumption}
                                        onChange={(e) => handleChange('drug_consumption', e.target.value)}
                                        rows={3}
                                        placeholder="List medications and supplements..."
                                    />
                                </div>
                            </div>
                            <div className="PME-sample-section">
                                <div className="PME-form-group">
                                    <label>Sample Acceptance Date</label>
                                    <input
                                        type="text"
                                        value={formData.sample_acceptance_date}
                                        onChange={(e) => handleChange('sample_acceptance_date', e.target.value)}
                                        placeholder="DD-MM-YYYY"
                                    />
                                </div>
                                <div className="PME-form-group">
                                    <label>Sample Type</label>
                                    <input
                                        type="text"
                                        value={formData.sample_type}
                                        onChange={(e) => handleChange('sample_type', e.target.value)}
                                        placeholder="Blood, Saliva, etc."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="PME-actions">
                            <button type="button" className="PME-button secondary" onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={`PME-button primary ${saveSuccess ? 'success' : ''}`}
                            >
                                {saveSuccess ? 'Saved!' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
                <div className="APM-modal">
                    <div className="APM-header">
                        <button
                            type="button"
                            className="APM-user-scroll-btn"
                            onClick={() => scrollAppUsers(-1)}
                            disabled={!appUserScrollState.canScrollLeft}
                            aria-label="Scroll app users left"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <div className="APM-user-scroll-window" ref={appUserScrollerRef}>
                            <div className="APM-user-circles">
                                {appUsers.map(user => (
                                    <div
                                        key={user.User_ID}
                                        className={`APM-user-circle ${selectedUser && user.User_ID === selectedUser.User_ID ? 'selected' : ''}`}
                                        onClick={() => setSelectedUser(user)}
                                        title={`${user.First_Name || ''} ${user.Last_Name || ''}`}
                                    >
                                        {(user.First_Name && user.First_Name.charAt(0).toUpperCase()) || ''}
                                        {(user.Last_Name && user.Last_Name.charAt(0).toUpperCase()) || ''}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <button
                            type="button"
                            className="APM-user-scroll-btn"
                            onClick={() => scrollAppUsers(1)}
                            disabled={!appUserScrollState.canScrollRight}
                            aria-label="Scroll app users right"
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                        <div className="APM-add-new-user-circle APM-chain-container" onClick={() => setShowInvitePopup(true)}>
                            <div className="APM-user-circle-icon">
                                <FontAwesomeIcon icon={faLink} />
                                <FontAwesomeIcon icon={faPlus} className="APM-user-circle-plus" />
                            </div>
                            {showInvitePopup && (
                                <div className="PME-invite-popup-tiny" onClick={(e) => e.stopPropagation()}>
                                    <div className="PME-invite-option-tiny" onClick={createNewUser}>
                                        <i className="fas fa-plus"></i>
                                        <div className='PME-text-invite-m'>Create Account</div>
                                    </div>
                                    <div
                                        className={`PME-invite-option-tiny ${!formData.phone ? 'disabled' : ''}`}
                                        onClick={formData.phone ? () => handleSendInvitation('Phone') : undefined}
                                    >
                                        <i className="fas fa-mobile-alt"></i>
                                        <div className='PME-text-invite-m'>Send Phone Invite</div>
                                        <div className="PME-invite-option-tiny-right">{formData.phone || 'No phone'}</div>
                                    </div>
                                    <div
                                        className={`PME-invite-option-tiny ${!formData.email ? 'disabled' : ''}`}
                                        onClick={formData.email ? () => handleSendInvitation('Email') : undefined}
                                    >
                                        <i className="fas fa-envelope"></i>
                                        <div className='PME-text-invite-m'>Send Email Invite</div>
                                        <div className="PME-invite-option-tiny-right">{formData.email || 'No email'}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="APM-content">
                        <AppProfileModal appUser={selectedUser} />
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
}

export default PatientMetadataEditor;
